/**
 * Auth Initialization Service
 * Handles automatic token refresh on app load/page refresh
 */

import { tokenStorage } from '../storage/tokenStorage';
import { authLogger } from '@shared/utils/logger';
import { env } from '@shared/config/env';

// Prevent duplicate initialization calls (e.g., from React StrictMode)
let initializationPromise: Promise<boolean> | null = null;

/**
 * Initialize authentication on app load
 * Attempts to refresh access token using refresh token from sessionStorage
 */
export const initializeAuth = async (): Promise<boolean> => {
  // Return existing promise if initialization is already in progress
  if (initializationPromise) {
    authLogger.log('Auth initialization already in progress, returning existing promise');
    return initializationPromise;
  }

  const refreshToken = tokenStorage.getRefreshToken();
  
  // No refresh token means user needs to login
  if (!refreshToken) {
    initializationPromise = null;
    return false;
  }

  // Create and store the initialization promise
  initializationPromise = (async () => {
    try {
    authLogger.log('Initializing auth: Refreshing access token...');
    
    // Add timeout to prevent hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    let response: Response;
    try {
      response = await fetch(
        `${env.API_ENDPOINT}/auth/refresh`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
          signal: controller.signal,
        }
      );
    } catch (networkError) {
      // Network error (offline, CORS, server down, timeout) — do NOT clear tokens.
      // The user's refresh token is still valid; let them stay logged in.
      clearTimeout(timeoutId);
      authLogger.error('Auth refresh network error (keeping tokens):', networkError);
      return false;
    }

    clearTimeout(timeoutId);

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        // Token is explicitly rejected by the server — clear it
        tokenStorage.clearTokens();
      }
      // For other HTTP errors (5xx etc.) keep tokens intact
      throw new Error(`Token refresh failed: ${response.status}`);
    }

    const result = await response.json();
    authLogger.log('Refresh response:', result);
    
    const data = result.data || result;
    const { accessToken, refreshToken: newRefreshToken, expiresIn, accessExpiryMillis } = data;

    // Support both field names from backend
    const expiryTime = expiresIn || accessExpiryMillis;

    if (!accessToken || !expiryTime) {
      authLogger.error('Missing required fields. Response structure:', {
        hasData: !!result.data,
        dataKeys: result.data ? Object.keys(result.data) : [],
        resultKeys: Object.keys(result),
        accessToken: !!accessToken,
        refreshToken: !!newRefreshToken,
        expiresIn: !!expiresIn,
        accessExpiryMillis: !!accessExpiryMillis
      });
      throw new Error('Invalid refresh response');
    }

    // Store both access and refresh tokens
    // Backend may return a new refresh token for security (token rotation)
    if (newRefreshToken) {
      tokenStorage.setTokens(accessToken, newRefreshToken, expiryTime);
      authLogger.log('Stored new access and refresh tokens');
    } else {
      // Fallback: only update access token if no new refresh token
      tokenStorage.setAccessToken(accessToken, expiryTime);
      authLogger.log('Stored new access token only');
    }
    
    authLogger.log('Auth initialized successfully');
    return true;
  } catch (error) {
    authLogger.error('Auth initialization failed:', error);
    // Tokens are only cleared above when the server explicitly returns 401/403.
    // Any other failure (5xx, malformed response) keeps tokens so the user
    // isn't logged out due to a transient server error.
    return false;
  } finally {
    // Reset promise after completion
    initializationPromise = null;
  }
  })();

  return initializationPromise;
};
